import { useState, useCallback } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { PageMeta } from "@/components/common/PageMeta";

const STORAGE_KEY = "cosx-investor-auth";
const DEFAULT_PASSWORD = "cosxai";

// Per-path password overrides (checked against the full pathname)
const PATH_PASSWORDS: Record<string, string> = {
  "/ir/dataroom/docs/CX-002-A": "cosxpitchdeck2026",
  "/ir/dataroom/docs/CX-002-B": "cosxpitchdeck2026",
  "/ir/dataroom/docs/CX-002-C": "cosxpitchdeck2026",
};

function getPathContext(pathname: string): { pw: string; storageKey: string } {
  for (const [path, pw] of Object.entries(PATH_PASSWORDS)) {
    if (pathname.startsWith(path)) {
      return { pw, storageKey: `${STORAGE_KEY}:${path}` };
    }
  }
  return { pw: DEFAULT_PASSWORD, storageKey: STORAGE_KEY };
}

function isAuthenticated(pathname: string): boolean {
  if (typeof window === "undefined") return false;
  // Viewer name must be on file — otherwise the watermark can't identify them.
  const name = localStorage.getItem("cosx-viewer-name");
  if (!name || !name.trim()) return false;
  const { pw, storageKey } = getPathContext(pathname);
  return localStorage.getItem(storageKey) === pw;
}

export function IRPasswordGate() {
  const { pathname } = useLocation();
  const [authed, setAuthed] = useState(() => isAuthenticated(pathname));
  const [name, setName] = useState("");
  const [value, setValue] = useState("");
  const [error, setError] = useState(false);

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      const { pw, storageKey } = getPathContext(pathname);
      if (value === pw && name.trim()) {
        localStorage.setItem(storageKey, pw);
        localStorage.setItem("cosx-viewer-name", name.trim());
        localStorage.setItem("cosx-viewer-ts", new Date().toISOString());
        setAuthed(true);
      } else {
        setError(true);
        setValue("");
      }
    },
    [value, name, pathname]
  );

  if (authed) return <Outlet />;

  return (
    <div className="min-h-screen flex items-center justify-center px-4">
      <PageMeta
        title="Investor Access | COSX"
        description="Enter the access code to view confidential investor materials."
        path="/ir/dataroom"
        noindex
      />
      <form onSubmit={handleSubmit} className="flex flex-col items-center gap-4 p-8 border border-[var(--color-border)] max-w-sm w-full">
        <div className="text-sm font-[family-name:var(--font-mono)] font-medium tracking-widest uppercase text-[var(--color-fg)]">
          Investor Access
        </div>
        <p className="text-xs text-[var(--color-fg-secondary)] text-center">
          This area is confidential. Please enter your name and access code to continue.
        </p>
        <input
          type="text"
          value={name}
          onChange={(e) => { setName(e.target.value); setError(false); }}
          placeholder="your name"
          autoFocus
          className="w-full px-4 py-2.5 border border-[var(--color-border)] bg-transparent text-[var(--color-fg)] font-[family-name:var(--font-mono)] text-sm text-center tracking-widest outline-none focus:border-[var(--color-fg-secondary)] transition-colors"
        />
        <input
          type="password"
          value={value}
          onChange={(e) => { setValue(e.target.value); setError(false); }}
          placeholder="access code"
          className="w-full px-4 py-2.5 border border-[var(--color-border)] bg-transparent text-[var(--color-fg)] font-[family-name:var(--font-mono)] text-sm text-center tracking-widest outline-none focus:border-[var(--color-fg-secondary)] transition-colors"
        />
        {error && (
          <p className="text-[10px] text-red-400 font-[family-name:var(--font-mono)]">
            Incorrect code. Try again.
          </p>
        )}
        <button
          type="submit"
          className="w-full py-2.5 text-xs font-medium bg-[var(--color-fg)] text-[var(--color-bg)] rounded-full hover:opacity-90 transition-opacity cursor-pointer"
        >
          Continue
        </button>
      </form>
    </div>
  );
}
